import React from 'react';
import styled from 'styled-components';

import { ATTR_OLIO_MAPPED_FIELDS } from 'constants/attrMappedFields';
import TabbedAdminListPage, { TabbedAdminListPageTab } from 'hooks/TabbedAdminListPage';
import { AdminListPageConfig } from 'hooks/useAdminListPage';
import { Attr } from 'models/flexible/Attr';
import { AttrValue } from 'models/flexible/AttrValue';
import { adminAttrsQueryKeys, adminIndexAttrs } from 'services/api/admin/attrs';
import { adminAttrValuesQueryKeys, adminIndexAttrValues } from 'services/api/admin/attrValues';
import { adminIndexClients } from 'services/api/admin/clients';
import type { AdminAttrIndexFields } from 'services/api/fields';
import type { AdminAttrValueIndexFields } from 'services/api/fields';
import PencilIcon from 'svg/PencilIcon';

const olioFieldOptions = ATTR_OLIO_MAPPED_FIELDS.map((f) => ({ id: f.value, name: f.label }));

function olioFieldLabel(value: string | null | undefined) {
  if (!value) return <>&mdash;</>;
  const label = ATTR_OLIO_MAPPED_FIELDS.find((f) => f.value === value)?.label;
  return label || <>&mdash;</>;
}

const ATTR_INCLUDES = ['client'] as const;

const attrsConfig: AdminListPageConfig<Attr<AdminAttrIndexFields<(typeof ATTR_INCLUDES)[number]>>> = {
  title: 'Attributes',
  queryKey: adminAttrsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexAttrs(params, signal, [...ATTR_INCLUDES]),
  includes: ATTR_INCLUDES,
  fetchParams: { defaultSort: 'name asc' },
  searchPlaceholder: 'Search Attributes',

  addButton: {
    label: 'Add Attribute',
    path: '/admin/attributes/new',
    permission: 'adminAttrCreate',
  },

  filters: [
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'olioAttr',
      label: 'Mapped Olio Field',
      type: 'dropdown',
      staticOptions: olioFieldOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'active',
      label: 'Show Only Inactive',
      type: 'checkbox',
      toRequest: (value) => (value ? 'false' : undefined),
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Raw Attribute',
      render: (row) => {
        if (!row.active) {
          return <InactiveText>{row.name}</InactiveText>;
        }
        return <BoldText>{row.name}</BoldText>;
      },
    },
    {
      id: 'olioAttr',
      header: 'Mapped Olio Field',
      render: (row) => olioFieldLabel(row.olioAttr),
    },
    {
      id: 'displayName',
      header: 'Display Attribute',
      render: (row) => row.displayName || <>&mdash;</>,
    },
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => {
        const isParent = !row.client?.leaf;
        return (
          <>
            {row.client?.name}
            {isParent && <ParentLabel> (Parent)</ParentLabel>}
          </>
        );
      },
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminAttrView',
      onClick: (row, { navigate }) => navigate(`/admin/attributes/${row.id}/edit`),
    },
  ],
};

const ATTR_VALUE_INCLUDES = ['client', 'attr'] as const;

const valuesConfig: AdminListPageConfig<AttrValue<AdminAttrValueIndexFields<(typeof ATTR_VALUE_INCLUDES)[number]>>> = {
  title: 'Values',
  queryKey: adminAttrValuesQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexAttrValues(params, signal, [...ATTR_VALUE_INCLUDES]),
  includes: ATTR_VALUE_INCLUDES,
  fetchParams: { defaultSort: 'name asc' },
  searchPlaceholder: 'Search Values',

  addButton: {
    label: 'Add Value',
    path: '/admin/attributes/values/new',
    permission: 'adminAttrValueCreate',
  },

  filters: [
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'attrValues', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'attrDisplayName',
      label: 'Raw Attribute',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminAttrs', 'filter'],
        fetchFn: adminIndexAttrs,
        params: { hasAttrValues: true, sortBy: 'name asc', distinctDisplayName: true },
      },
      getOptionLabel: (o) => o.displayName ?? o.name,
      getOptionValue: (o) => o.displayName ?? o.name,
      toRequest: (value) => {
        if (!value?.displayName || value.displayName === 'All') return undefined;
        return value.displayName;
      },
    },
    {
      key: 'olioAttr',
      label: 'Mapped Olio Field',
      type: 'dropdown',
      staticOptions: olioFieldOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Raw Value',
      render: (row) => {
        if (!row.active) {
          return <InactiveText>{row.name}</InactiveText>;
        }
        return <BoldText>{row.name}</BoldText>;
      },
    },
    {
      id: 'attr_name',
      header: 'Raw Attribute',
      render: (row) => row.attr?.name ?? '',
    },
    {
      id: 'olioAttr',
      header: 'Mapped Olio Field',
      render: (row) => olioFieldLabel(row.attr?.olioAttr),
    },
    {
      id: 'displayName',
      header: 'Display Value',
      render: (row) => row.displayName || <>&mdash;</>,
    },
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => {
        const isParent = !row.client?.leaf;
        return (
          <>
            {row.client?.name}
            {isParent && <ParentLabel> (Parent)</ParentLabel>}
          </>
        );
      },
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminAttrValueView',
      onClick: (row, { navigate }) => navigate(`/admin/attributes/values/${row.id}/edit`),
    },
  ],
};

const tabs: TabbedAdminListPageTab[] = [
  { label: 'Attributes', path: '/admin/attributes', permission: 'adminAttrView', config: attrsConfig },
  { label: 'Values', path: '/admin/attributes/values', permission: 'adminAttrValueView', config: valuesConfig },
];

export default function Attributes() {
  return <TabbedAdminListPage pageTitle='Attributes' tabs={tabs} />;
}

const BoldText = styled.span`
  font-weight: 700;
`;

const InactiveText = styled.span`
  font-weight: 700;
  color: var(--black-25);
`;

const ParentLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: var(--primary-blue);
  margin-left: 4px;
`;
